import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { LoanAgreement } from "@/contracts";

import { attestSignatureMutation } from "@/api/queries";
import { Alert } from "@/components/ui/alert";
import { TextAreaField, TextField } from "@/components/ui/field";
import { FormDialog } from "@/components/ui/form-dialog";
import { useApiClient } from "@/lib/api-context";

type FormValues = {
  signedOn: string;
  notes: string;
};

/**
 * Recording that the customer signed the printed copy.
 *
 * The dialog spends a paragraph on what this is not, for the same reason the
 * acknowledge dialog does: the obvious reading of a tick next to "signed" is
 * that the platform holds a signature, and it does not. It holds a member of
 * staff's statement that a paper exists, and if that paper is lost there is
 * nothing here to fall back on (ADR-074).
 */
export function AttestSignatureDialog({
  agreement,
  onClose,
}: {
  agreement: LoanAgreement;
  onClose: () => void;
}) {
  const { t } = useTranslation("loans");
  const client = useApiClient();
  const queryClient = useQueryClient();

  const formSchema = z.object({
    signedOn: z.string().min(1, t("enterDate")),
    notes: z.string().trim().max(500, t("notesTooLong")),
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { signedOn: new Date().toISOString().slice(0, 10), notes: "" },
  });

  const mutation = useMutation({
    ...attestSignatureMutation(client),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["loan-agreements"] });
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    await mutation.mutateAsync({
      id: agreement.id,
      body: {
        signedOn: values.signedOn,
        ...(values.notes ? { notes: values.notes } : {}),
      },
    });

    onClose();
  });

  return (
    <FormDialog
      open
      title={t("attestTitle")}
      description={t("attestDescription", { number: agreement.number })}
      submitLabel={t("attestSubmit")}
      pending={isSubmitting || mutation.isPending}
      onSubmit={(event) => void onSubmit(event)}
      onCancel={onClose}
    >
      <div className="flex flex-col gap-4">
        {mutation.isError ? <Alert>{mutation.error.message}</Alert> : null}

        <TextField
          label={t("attestDate")}
          type="date"
          hint={t("attestDateHint")}
          max={new Date().toISOString().slice(0, 10)}
          required
          {...(errors.signedOn?.message === undefined ? {} : { error: errors.signedOn.message })}
          {...register("signedOn")}
        />

        <TextAreaField
          label={t("attestNotes")}
          {...(errors.notes?.message === undefined ? {} : { error: errors.notes.message })}
          {...register("notes")}
        />
      </div>
    </FormDialog>
  );
}
